/* eslint-disable jsx-a11y/tabindex-no-positive */
import compact from 'lodash/compact';
import { arrayOf, bool, func, number, string } from 'prop-types';
import React from 'react';
import { GoArrowBoth } from 'react-icons/go';
import {
  Button,
  Input,
  Label,
  UncontrolledTooltip as Tooltip,
} from 'reactstrap';

import CurrencyInput from '../../generic/CurrencyInput';
import ShareInput from '../../generic/ShareInput';
import SingleSelect from '../../generic/SingleSelect';
import InfoTooltip from '../../generic/Tooltip/InfoTooltip';
import {
  getOperativeSystem,
  OPERATIVE_SYSTEM_TYPES,
} from '../../products/constants';
import { isCCP, isOffsite, isOnsite } from '../../products/utils';
import {
  offSiteMinBudgetShape,
  optionShape,
  quoteLineShape,
} from '../../prop-types';
import { COLORS } from '../../utils/constants';
import CountrySplitCopySelect from '../CountrySplitCopySelect.container';
import DetailsPopover from '../DetailsPopover.container';
import MissingSKUs from '../MissingSKUs.container';
import OffSiteMinBudgetCalculator from '../OffSiteMinSpendCalculator';
import {
  calculateGrossOfferValue,
  calculateSpendableBudgetFromQL,
} from '../utils/calculate-derived-values';
import DerivedShareInput from '../utils/DerivedShareInput';
import HELP_TEXTS from '../utils/help-texts';
import CountrySplit from './CountrySplit';

// If you change the row layout, please remember to also adjust the headline in the QuoteLineTable component.
function QuoteLineRow({
  model,
  netClientBudgetExternal,
  wkzBudget,
  headerDiscountPercent,
  id,
  isSelected,
  onChange,
  onChangeSelection,
  onChangeCountrySplit,
  onSplitCountrySharesEqually,
  onRemove,
  onDuplicate,
  productCode,
  productName,
  productGroup,
  productBusiness,
  productOperativeSystem,
  supportsProductTags,
  offSiteMinBudgetSpecification,
  pricingOptions,
  withAdjustedFee,
  withFreeService,
  isEditing,
  isDraftCampaignEnabled,
}) {
  const spendableBudget = calculateSpendableBudgetFromQL(
    model,
    headerDiscountPercent,
  );
  const grossOfferValue = calculateGrossOfferValue(
    model,
    headerDiscountPercent,
  );
  const countCountriesWithShare = compact(
    Object.values(model.countrySplit),
  ).length;
  const isOffsiteBusiness = isOffsite({ business: productBusiness });
  const isOnsiteBusiness = isOnsite({ business: productBusiness });
  const isZmsAm =
    productOperativeSystem ===
    getOperativeSystem(isDraftCampaignEnabled).ccp.system;
  const disabled = !isEditing || isZmsAm;

  return (
    <tr id={id}>
      <td>
        {isDraftCampaignEnabled && !isZmsAm && (
          <>
            <Input
              id={`quoteLines.${model.id}.selected`}
              onChange={onChangeSelection}
              data-quote-line-id={model.id}
              className="position-static mx-2"
              type="checkbox"
              checked={isSelected}
              disabled={disabled}
            />
            <Label for={`quoteLines.${model.id}.selected`} hidden>
              Select/deselect line
            </Label>
          </>
        )}
        {isDraftCampaignEnabled && isZmsAm && (
          <InfoTooltip target={`disabled-ccp-campaign-tooltip-${model.id}`}>
            {HELP_TEXTS.tooltips.disabledCcpCampaign}
          </InfoTooltip>
        )}

        {!isDraftCampaignEnabled && (
          <>
            <Input
              id={`quoteLines.${model.id}.selected`}
              onChange={onChangeSelection}
              data-quote-line-id={model.id}
              className="position-static mx-2"
              type="checkbox"
              checked={isSelected}
              disabled={!isEditing}
            />
            <Label for={`quoteLines.${model.id}.selected`} hidden>
              Select/deselect line
            </Label>
          </>
        )}
      </td>
      <td className="text-nowrap">
        <Button
          outline
          color="danger"
          onClick={onRemove}
          data-quote-line-id={model.id}
          className="qa-quote-line-remove-button"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        >
          –
        </Button>
        <Button
          outline
          onClick={onDuplicate}
          data-quote-line-id={model.id}
          className="qa-quote-line-duplicate-button"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        >
          +
        </Button>
      </td>
      <td className="sticky-left">
        <div className="small">
          {productBusiness}, {productGroup}
        </div>
        {productName}
        {productOperativeSystem === OPERATIVE_SYSTEM_TYPES.zmsAm.system &&
          !isCCP({ code: productCode }) && (
            <InfoTooltip target={`zmsam-countries-tooltip-${model.id}`}>
              {HELP_TEXTS.tooltips.zmsAmCountriesNotSupported}
            </InfoTooltip>
          )}
        {supportsProductTags && (
          <div>
            <Input
              bsSize="sm"
              id={`quoteLines.${model.id}.productTag`}
              onChange={onChange}
              name="productTag"
              value={model.productTag || ''}
              data-quote-line-id={model.id}
              type="text"
              disabled={isDraftCampaignEnabled ? disabled : !isEditing}
            />
          </div>
        )}
      </td>
      <td>
        {pricingOptions.length > 1 && (
          <SingleSelect
            value={model.pricing}
            options={pricingOptions}
            onChange={onChange}
            id={`quoteLines.${model.id}.pricing`}
            name="pricing"
            data-quote-line-id={model.id}
            readOnly={isDraftCampaignEnabled ? disabled : !isEditing}
          />
        )}
        {pricingOptions.length <= 1 && (
          <Input
            id={`quoteLines.${model.id}.pricing`}
            name="pricing"
            type="text"
            value={model.pricing}
            readOnly
            disabled={isDraftCampaignEnabled ? disabled : !isEditing}
          />
        )}
      </td>
      <td>
        <Label for={`quoteLines.${model.id}.netClientBudgetExternal`} hidden>
          Net Client Budget External
        </Label>

        <CurrencyInput
          bsSize="sm"
          id={`quoteLines.${model.id}.netClientBudgetExternal`}
          name="netClientBudgetExternal"
          data-quote-line-id={model.id}
          value={model.netClientBudgetExternal || 0}
          onChange={onChange}
          tabIndex="2"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
        <Label for={`quoteLines.${model.id}.budgetShare`} hidden>
          Percentage budget share
        </Label>
        <DerivedShareInput
          bsSize="sm"
          id={`quoteLines.${model.id}.budgetShare`}
          value={model.netClientBudgetExternal}
          budgetTotal={netClientBudgetExternal}
          name="budgetShare"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
      </td>
      <td>
        <Label for={`quoteLines.${model.id}.wkzBudget`} hidden>
          WKZ Budget
        </Label>

        <CurrencyInput
          bsSize="sm"
          id={`quoteLines.${model.id}.wkzBudget`}
          name="wkzBudget"
          data-quote-line-id={model.id}
          value={model.wkzBudget || 0}
          onChange={onChange}
          tabIndex="2"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
        <Label for={`quoteLines.${model.id}.budgetShare`} hidden>
          Percentage budget share
        </Label>
        <DerivedShareInput
          bsSize="sm"
          id={`quoteLines.${model.id}.budgetShare`}
          value={model.wkzBudget}
          budgetTotal={wkzBudget}
          name="budgetShare"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
      </td>
      <td>
        <Label for={`quoteLines.${model.id}.coInvest`} hidden>
          Co-Invest
        </Label>
        <CurrencyInput
          bsSize="sm"
          id={`quoteLines.${model.id}.coInvest`}
          name="coInvest"
          data-quote-line-id={model.id}
          value={model.coInvest || 0}
          onChange={onChange}
          tabIndex="3"
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
      </td>
      <td>
        <Label for={`quoteLines.${model.id}.freeService`} hidden>
          Free Service
        </Label>
        <CurrencyInput
          bsSize="sm"
          id={`quoteLines.${model.id}.freeService`}
          name="freeService"
          data-quote-line-id={model.id}
          value={model.freeService || 0}
          onChange={onChange}
          tabIndex="3"
          disabled={
            !withFreeService || (isDraftCampaignEnabled ? disabled : !isEditing)
          }
        />
      </td>
      <td>
        <CurrencyInput bsSize="sm" value={grossOfferValue} readOnly />
      </td>
      <td>
        <Label for={`quoteLines.${model.id}.adjustedFee`} hidden>
          Adj. Fee
        </Label>
        <ShareInput
          bsSize="sm"
          id={`quoteLines.${model.id}.adjustedFee`}
          name="adjustedFee"
          data-quote-line-id={model.id}
          value={model.adjustedFee || 0}
          onChange={onChange}
          tabIndex="3"
          disabled={
            !withAdjustedFee || (isDraftCampaignEnabled ? disabled : !isEditing)
          }
        />
        <Tooltip
          placement="right"
          target={`quoteLines\\.${model.id}\\.adjustedFee`}
        >
          Original Fee: {model.originalFee * 100}%
        </Tooltip>
      </td>
      <td>
        <CurrencyInput bsSize="sm" value={spendableBudget} readOnly />
        {isOffsiteBusiness && (
          <div className="mt-1 small text-right">
            <OffSiteMinBudgetCalculator
              id={model.id}
              specification={offSiteMinBudgetSpecification}
              startDate={model.startDate}
              endDate={model.endDate}
              countriesCount={countCountriesWithShare}
              serviceValue={spendableBudget}
            />
          </div>
        )}
      </td>
      <td>
        <MissingSKUs id={model.id}>
          <DetailsPopover
            id={model.id}
            isDisabled={isDraftCampaignEnabled ? disabled : !isEditing}
          />
        </MissingSKUs>
      </td>
      <td>
        <CountrySplitCopySelect
          id={model.id}
          disabled={isDraftCampaignEnabled ? disabled : !isEditing}
        />
      </td>
      <td key="splitEqually">
        {!isOnsiteBusiness && !isOffsiteBusiness && (
          <Button
            color={COLORS.SECONDARY}
            title="Split budget equally across countries"
            className="px-2 py-1"
            onClick={() => onSplitCountrySharesEqually(model.id)}
            disabled={isDraftCampaignEnabled ? disabled : !isEditing}
          >
            <GoArrowBoth className="va-n1" />
          </Button>
        )}
      </td>
      <CountrySplit
        value={model.countrySplit}
        onChange={onChangeCountrySplit}
        onSplitCountrySharesEqually={onSplitCountrySharesEqually}
        total={spendableBudget}
        id={`quoteLines.${model.id}.countrySplit`}
        name={`quoteLines.${model.id}.countrySplit`}
        quoteLineId={model.id}
        disableAll={isDraftCampaignEnabled ? disabled : !isEditing}
        productCode={productCode}
        productOperativeSystem={productOperativeSystem}
      />
    </tr>
  );
}

QuoteLineRow.propTypes = {
  model: quoteLineShape.isRequired,
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  headerDiscountPercent: number.isRequired,
  id: string.isRequired,

  isSelected: bool.isRequired,

  onChange: func.isRequired,
  onChangeSelection: func.isRequired,
  onChangeCountrySplit: func.isRequired,
  onSplitCountrySharesEqually: func.isRequired,
  onRemove: func.isRequired,
  onDuplicate: func.isRequired,

  // From the product specification
  productCode: string.isRequired,
  productName: string.isRequired,
  productGroup: string.isRequired,
  productBusiness: string,
  productOperativeSystem: string.isRequired,
  supportsProductTags: bool.isRequired,
  offSiteMinBudgetSpecification: offSiteMinBudgetShape.isRequired,
  pricingOptions: arrayOf(optionShape).isRequired,
  withAdjustedFee: bool.isRequired,
  withFreeService: bool.isRequired,
  isEditing: bool,
  isDraftCampaignEnabled: bool.isRequired,
};

QuoteLineRow.defaultProps = {
  productBusiness: '',
  isEditing: true,
};

export default QuoteLineRow;
